'use client';

import React, { useState, useEffect, useCallback } from 'react';
import Link from 'next/link';

/* ─── Types ─────────────────────────────────────────────────── */
type Lang = 'en' | 'ar' | 'fr';
type OfferState = 'active' | 'expiring' | 'expired';

interface UrgencyTranslations {
  title: string;
  discount: string;
  expiresIn: string;
  claimOffer: string;
  claimShort: string;
  expiringSoon: string;
  expired: string;
  specialOffer: string;
  demoLabel: string;
}

/* ─── Translation Keys ──────────────────────────────────────── */
const urgencyTranslations: Record<Lang, UrgencyTranslations> = {
  en: {
    title: 'Limited Offer',
    discount: '80% OFF',
    expiresIn: 'Expires in',
    claimOffer: 'Claim Offer →',
    claimShort: 'Claim',
    expiringSoon: 'Offer expires soon!',
    expired: 'This offer has expired.',
    specialOffer: 'Special limited-time offer.',
    demoLabel: 'Demo',
  },
  ar: {
    title: 'عرض محدود',
    discount: 'خصم 80%',
    expiresIn: 'ينتهي خلال',
    claimOffer: 'استفد من العرض →',
    claimShort: 'استفد',
    expiringSoon: 'ينتهي قريباً!',
    expired: 'انتهى هذا العرض.',
    specialOffer: 'عرض خاص لمدة محدودة.',
    demoLabel: 'تجريبي',
  },
  fr: {
    title: 'Offre limitée',
    discount: '-80%',
    expiresIn: "Expire dans",
    claimOffer: "Profiter →",
    claimShort: 'Profiter',
    expiringSoon: "Expire bientôt !",
    expired: 'Cette offre a expiré.',
    specialOffer: 'Offre spéciale limitée.',
    demoLabel: 'Démo',
  },
};

/* ─── Constants ─────────────────────────────────────────────── */
const OFFER_DURATION_MS = 24 * 60 * 60 * 1000;
const STORAGE_KEY = 'wealthos_offer_end_time';
const ONE_HOUR_MS = 60 * 60 * 1000;

/* ─── Analytics Helper ──────────────────────────────────────── */
function trackEvent(event: string, lang: Lang) {
  if (typeof window !== 'undefined' && (window as any).gtag) {
    (window as any).gtag('event', event, {
      event_category: 'urgency_banner',
      event_label: lang,
      language: lang,
    });
  }
}

/* ─── Countdown Formatter ───────────────────────────────────── */
function formatTime(ms: number): string {
  if (ms <= 0) return '00:00:00';
  const totalSeconds = Math.floor(ms / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return [hours, minutes, seconds]
    .map((v) => String(v).padStart(2, '0'))
    .join(':');
}

/* ─── UrgencyBanner Component ───────────────────────────────── */
interface UrgencyBannerProps {
  lang: Lang;
  isRtl: boolean;
}

export default function UrgencyBanner({ lang, isRtl }: UrgencyBannerProps) {
  const [timeLeft, setTimeLeft] = useState<number>(OFFER_DURATION_MS);
  const [offerState, setOfferState] = useState<OfferState>('active');
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [mounted, setMounted] = useState(false);

  const t = urgencyTranslations[lang];

  useEffect(() => {
    setMounted(true);
    const stored = localStorage.getItem(STORAGE_KEY);
    let endTime: number;

    if (stored) {
      endTime = parseInt(stored, 10);
      if (endTime <= Date.now()) {
        endTime = Date.now() + OFFER_DURATION_MS;
        localStorage.setItem(STORAGE_KEY, String(endTime));
      }
    } else {
      endTime = Date.now() + OFFER_DURATION_MS;
      localStorage.setItem(STORAGE_KEY, String(endTime));
    }

    const remaining = endTime - Date.now();
    setTimeLeft(remaining);

    if (remaining <= 0) {
      setOfferState('expired');
    } else if (remaining < ONE_HOUR_MS) {
      setOfferState('expiring');
    } else {
      setOfferState('active');
    }

    const showTimer = setTimeout(() => setVisible(true), 1500);
    return () => clearTimeout(showTimer);
  }, []);

  useEffect(() => {
    if (!mounted || offerState === 'expired') return;

    const interval = setInterval(() => {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (!stored) return;
      let endTime = parseInt(stored, 10);
      const remaining = endTime - Date.now();

      if (remaining <= 0) {
        setTimeLeft(0);
        setOfferState('expired');
        clearInterval(interval);
      } else {
        setTimeLeft(remaining);
        if (remaining < ONE_HOUR_MS) {
          setOfferState('expiring');
        }
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [mounted, offerState]);

  useEffect(() => {
    if (visible && mounted) {
      trackEvent('offer_viewed', lang);
    }
  }, [visible, mounted, lang]);

  const handleClaim = useCallback(() => {
    trackEvent('offer_clicked', lang);
    trackEvent('checkout_started', lang);
  }, [lang]);

  const handleDismiss = useCallback(() => {
    setDismissed(true);
  }, []);

  if (!mounted || dismissed) return null;

  const isExpired = offerState === 'expired';
  const isExpiring = offerState === 'expiring';

  const bannerBg = isExpired
    ? 'bg-muted/95 border-border/60'
    : isExpiring
    ? 'bg-gradient-to-r from-orange-100/95 via-red-100/95 to-orange-100/95 dark:from-orange-950/95 dark:via-red-950/95 dark:to-orange-950/95 border-orange-500/40' :'bg-gradient-to-r from-background/98 via-primary/8 to-background/98 border-primary/30';

  return (
    <div
      className={`fixed bottom-0 left-0 right-0 z-[60] transition-all duration-500 ease-out ${
        visible ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0'
      }`}
      dir={isRtl ? 'rtl' : 'ltr'}
      role="banner"
      aria-label={t.title}
      style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
    >
      {/* Top border glow */}
      <div
        className={`absolute top-0 left-0 right-0 h-px ${
          isExpired
            ? 'bg-border/40'
            : isExpiring
            ? 'bg-gradient-to-r from-transparent via-orange-400/60 to-transparent' :'bg-gradient-to-r from-transparent via-primary/50 to-transparent'
        }`}
      />

      <div
        className={`${bannerBg} backdrop-blur-xl border-t shadow-2xl`}
        style={isExpiring ? { boxShadow: '0 -4px 24px rgba(249,115,22,0.15)' } : {}}
      >
        {/* ── Desktop layout (sm+) ── */}
        <div className="hidden sm:flex items-center justify-between max-w-7xl mx-auto px-4 sm:px-6 py-3 gap-3 lg:gap-4">
          {/* Left: Icon + Title + Discount */}
          <div className={`flex items-center gap-2 lg:gap-3 flex-shrink-0 min-w-0 ${isRtl ? 'flex-row-reverse' : ''}`}>
            {!isExpired && (
              <span className={`text-base lg:text-lg flex-shrink-0 ${isExpiring ? 'animate-pulse' : ''}`}>🔥</span>
            )}
            {isExpired ? (
              <span className="text-sm font-semibold text-muted-foreground truncate">{t.expired}</span>
            ) : (
              <>
                <span className={`text-xs font-bold uppercase tracking-wide flex-shrink-0 ${isExpiring ? 'text-orange-500 dark:text-orange-400' : 'text-amber-700 dark:text-primary'}`}>
                  {isExpiring ? t.expiringSoon : t.title}
                </span>
                <span
                  className={`text-sm lg:text-base font-black flex-shrink-0 ${isExpiring ? 'text-orange-600 dark:text-orange-300' : 'text-amber-800 dark:text-primary'}`}
                  style={{ textShadow: isExpiring ? '0 0 12px rgba(249,115,22,0.5)' : '0 0 12px rgba(201,162,39,0.4)' }}
                >
                  {t.discount}
                </span>
              </>
            )}
          </div>

          {/* Center: Countdown */}
          {!isExpired && (
            <div className={`flex items-center gap-2 flex-shrink-0 ${isRtl ? 'flex-row-reverse' : ''}`}>
              <span className="text-xs text-muted-foreground hidden md:block">{t.expiresIn}</span>
              <div
                className={`font-mono text-sm lg:text-base font-bold tabular-nums px-2 lg:px-3 py-1 rounded-lg border ${
                  isExpiring
                    ? 'text-orange-700 dark:text-orange-300 border-orange-500/30 bg-orange-100/80 dark:bg-orange-950/50' :'text-amber-800 dark:text-primary border-primary/30 bg-primary/10'
                }`}
              >
                {formatTime(timeLeft)}
              </div>
            </div>
          )}

          {/* Right: CTA + Dismiss */}
          <div className={`flex items-center gap-2 lg:gap-3 flex-shrink-0 ${isRtl ? 'flex-row-reverse' : ''}`}>
            {!isExpired && (
              <Link
                href={`/register?plan=pro&source=urgency_banner&lang=${lang}`}
                onClick={handleClaim}
                className={`flex items-center gap-1.5 px-4 lg:px-5 py-2 rounded-xl font-bold text-xs lg:text-sm transition-all hover:-translate-y-0.5 active:scale-95 shadow-lg whitespace-nowrap ${
                  isExpiring
                    ? 'bg-gradient-to-r from-orange-500 to-red-500 text-white hover:opacity-90 shadow-orange-500/30'
                    : 'gold-gradient text-primary-foreground hover:opacity-90 shadow-primary/30'
                }`}
              >
                {t.claimOffer}
              </Link>
            )}
            <button
              onClick={handleDismiss}
              className="w-7 h-7 rounded-full flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted/60 transition-all text-xs flex-shrink-0"
              aria-label="Dismiss banner"
            >
              ✕
            </button>
          </div>
        </div>

        {/* ── Mobile layout (< sm) ── */}
        <div className="sm:hidden px-3 py-2.5">
          {isExpired ? (
            <div className={`flex items-center justify-between gap-2 ${isRtl ? 'flex-row-reverse' : ''}`}>
              <span className="text-xs text-muted-foreground">{t.expired}</span>
              <button
                onClick={handleDismiss}
                className="w-6 h-6 flex items-center justify-center text-muted-foreground text-xs"
                aria-label="Dismiss"
              >✕</button>
            </div>
          ) : (
            <div className={`flex items-center gap-2 ${isRtl ? 'flex-row-reverse' : ''}`}>
              {/* Fire + Discount */}
              <div className={`flex items-center gap-1 flex-shrink-0 ${isRtl ? 'flex-row-reverse' : ''}`}>
                <span className={`text-sm ${isExpiring ? 'animate-pulse' : ''}`}>🔥</span>
                <span className={`text-sm font-black flex-shrink-0 ${isExpiring ? 'text-orange-300' : 'text-primary'}`}>
                  {t.discount}
                </span>
              </div>
              {/* Countdown */}
              <div
                className={`font-mono text-xs font-bold tabular-nums px-2 py-1 rounded-lg border flex-shrink-0 ${
                  isExpiring
                    ? 'text-orange-300 border-orange-500/30 bg-orange-950/50' :'text-primary border-primary/30 bg-primary/10'
                }`}
              >
                {formatTime(timeLeft)}
              </div>
              {/* Spacer */}
              <div className="flex-1 min-w-0" />
              {/* CTA */}
              <Link
                href={`/register?plan=pro&source=urgency_banner&lang=${lang}`}
                onClick={handleClaim}
                className={`flex items-center px-3 py-1.5 rounded-lg font-bold text-xs transition-all active:scale-95 flex-shrink-0 whitespace-nowrap ${
                  isExpiring
                    ? 'bg-gradient-to-r from-orange-500 to-red-500 text-white' :'gold-gradient text-primary-foreground'
                }`}
              >
                {t.claimShort}
              </Link>
              {/* Dismiss */}
              <button
                onClick={handleDismiss}
                className="w-6 h-6 flex items-center justify-center text-muted-foreground text-xs flex-shrink-0"
                aria-label="Dismiss"
              >✕</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
